import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material';
import { IconDownload } from '@tabler/icons-react';

import { IconButton, Tooltip } from '@zendesk/forethought-elements';
import { useEmitTrackingEventCallback } from 'src/hooks/hooks';
import { selectCurrentUser } from 'src/reducers/userReducer/userReducer';
import { sendAuthRequest } from 'src/services/api';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { INSIGHTS_TIMEZONE } from 'src/utils/insightsTime';

interface InsightDownloadCsvProps {
  filename: string;
  requestData?: Record<string, unknown>;
  url: string;
}

const InsightDownloadCsv: React.FC<
  React.PropsWithChildren<InsightDownloadCsvProps>
> = ({ filename, requestData, url }) => {
  const { t } = useTranslation('solve-insights');
  const user = useSelector(selectCurrentUser) || {};
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = React.useState(false);

  const emitTrackingEventCallback = useEmitTrackingEventCallback();

  const downloadFile = ({
    filename,
    onError,
    url,
  }: {
    filename: string;
    onError?: () => void;
    url: string;
  }) => {
    setIsDownloading(true);
    emitTrackingEventCallback('insight-download-csv', {
      filename,
      url,
    });
    // Insights bucket in one fixed zone so every viewer's export matches the
    // numbers on screen (FTSV-1332)
    const requestDataWithTimezone = {
      ...requestData,
      timezone: INSIGHTS_TIMEZONE,
    };
    sendAuthRequest(url, requestDataWithTimezone, 'POST')
      .then(() => {
        dispatch(
          setGlobalToastOptions({
            title: t('common.insightDownloadCsv.reportToast', {
              email: user?.user?.user?.email,
            }),
            variant: 'main',
          }),
        );
      })
      .catch(err => {
        console.error(err);
        onError?.();
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <Tooltip tooltipContent={t('common.insightDownloadCsv.exportCsv')}>
      <IconButton
        aria-label={t('common.insightDownloadCsv.exportCsv')}
        disabled={isDownloading}
        isLoading={isDownloading}
        onClick={() => downloadFile({ filename, url })}
        variant='ghost'
      >
        <IconDownload color={theme.palette.primary.main} />
      </IconButton>
    </Tooltip>
  );
};

export default InsightDownloadCsv;
